import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { Calculator, ShieldCheck, ArrowRight, Info, Plus, Trash, CircleNotch } from "@phosphor-icons/react";
import { mapUnavailableReason, LOAD_ASSIST_UNAVAILABLE_TITLE, LOAD_ASSIST_UNAVAILABLE_BODY } from "../../lib/estimateUnavailable";
import { QuoteWeightRateFooter } from "../QuoteWeightRateFooter";
import { pushEvent, currentPagePath, weightBand, packageCountBand } from "../../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function readUtm() {
  try {
    const p = new URLSearchParams(window.location.search);
    const utm = {};
    ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid"].forEach((k) => {
      const v = p.get(k);
      if (v) utm[k] = v;
    });
    return Object.keys(utm).length ? utm : null;
  } catch { return null; }
}

const emptyLine = () => ({ quantity: "", actual_weight_kg_per_box: "", length_cm: "", width_cm: "", height_cm: "", package_type: "CARTON_BOX" });

const PACKAGE_TYPES = [
  { value: "CARTON_BOX", label: "Carton / Box" },
  { value: "BAG_SACK", label: "Bag / Sack" },
  { value: "CRATE", label: "Crate" },
  { value: "BUNDLE", label: "Bundle" },
  { value: "ROLL_TUBE", label: "Roll / Tube" },
  { value: "OTHER", label: "Other" },
];
const emptyForm = () => ({ email: "", phone: "", pickup_address: "", pickup_pincode: "", delivery_address: "", delivery_pincode: "" });

const inr = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n);

const LOAD_ASSIST_OPTIONS = [
  { value: "none", label: "No" },
  { value: "pickup", label: "Pickup loading only" },
  { value: "delivery", label: "Delivery unloading only" },
  { value: "both", label: "Both pickup & delivery" },
];

export default function CheckPriceDrawer({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm());
  const [lines, setLines] = useState([emptyLine()]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [enquiry, setEnquiry] = useState({ submitting: false, done: false });
  const [loadSide, setLoadSide] = useState("none");

  // Analytics one-shot guards (Phase-1 dataLayer). Reset on drawer reset/close.
  const formStartFired = useRef(false);
  const continueFired = useRef(false);
  const markFormStart = () => {
    if (formStartFired.current) return;
    formStartFired.current = true;
    pushEvent("estimate_form_start", { page_path: currentPagePath(), placement: "check_price_drawer", form_name: "check_price" });
  };
  const packageCountTotal = () => lines.reduce((s, l) => s + (Number(l.quantity) || 0), 0);

  const setField = (k) => (e) => { markFormStart(); setForm((f) => ({ ...f, [k]: e.target.value })); };
  const setLine = (i, k) => (e) => { markFormStart(); setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: e.target.value } : l))); };
  const addLine = () => setLines((ls) => [...ls, emptyLine()]);
  const removeLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));

  const reset = () => { setForm(emptyForm()); setLines([emptyLine()]); setResult(null); setError(""); setLoading(false); setEnquiry({ submitting: false, done: false }); setLoadSide("none"); formStartFired.current = false; continueFired.current = false; };
  const close = (v) => { onOpenChange(v); if (!v) reset(); };

  const validate = () => {
    const emailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    if (!emailRe.test(form.email.trim())) return "Enter a valid email to view your estimate.";
    if (!form.pickup_address.trim() || !form.delivery_address.trim()) return "Pickup and delivery addresses are required.";
    for (const l of lines) {
      const vals = [l.quantity, l.actual_weight_kg_per_box, l.length_cm, l.width_cm, l.height_cm];
      if (vals.some((v) => v === "" || Number(v) <= 0)) return "For every package line, quantity, weight and all dimensions must be greater than zero.";
    }
    return "";
  };

  const runEstimate = async (e) => {
    e.preventDefault();
    setError("");
    const v = validate();
    if (v) { setError(v); return; }
    setLoading(true);
    try {
      const payload = {
        requester_email: form.email.trim(),
        requester_phone: form.phone.trim() || null,
        pickup_address: form.pickup_address.trim(),
        pickup_pincode: form.pickup_pincode.trim() || null,
        delivery_address: form.delivery_address.trim(),
        delivery_pincode: form.delivery_pincode.trim() || null,
        packages: lines.map((l) => ({
          quantity: Number(l.quantity),
          actual_weight_kg_per_box: Number(l.actual_weight_kg_per_box),
          length_cm: Number(l.length_cm),
          width_cm: Number(l.width_cm),
          height_cm: Number(l.height_cm),
          package_type: l.package_type || "CARTON_BOX",
        })),
        load_assist_side: loadSide,
        utm: readUtm(),
      };
      const r = await axios.post(`${API}/public/estimate`, payload);
      setResult(r.data);
      if (r.data && r.data.status === "estimate_available") {
        pushEvent("estimate_generated", {
          page_path: currentPagePath(), placement: "check_price_drawer", form_name: "check_price",
          estimate_available: true,
          route_label: r.data.route_label,
          pickup_zone: r.data.pickup_zone,
          delivery_zone: r.data.delivery_zone,
          weight_band: weightBand(r.data.total_chargeable_weight_kg),
          package_count_band: packageCountBand(packageCountTotal()),
          load_assist_side: loadSide,
        });
      } else {
        pushEvent("estimate_unavailable", {
          page_path: currentPagePath(), placement: "check_price_drawer", form_name: "check_price",
          estimate_available: false,
          reason_code: (r.data && r.data.reason_codes && r.data.reason_codes[0]) || "PRICE_UNAVAILABLE",
          weight_band: weightBand(r.data && r.data.total_chargeable_weight_kg),
          package_count_band: packageCountBand(packageCountTotal()),
        });
      }
    } catch (err) {
      // Never show a fake price. On any error (e.g. validation/network), fall back to the
      // reason-mapped unavailable state — NOT a "sign in for a confirmed price" prompt.
      setResult({ status: "unavailable", reason_codes: ["ESTIMATE_UNAVAILABLE"] });
      pushEvent("estimate_unavailable", {
        page_path: currentPagePath(), placement: "check_price_drawer", form_name: "check_price",
        estimate_available: false, reason_code: "PRICE_UNAVAILABLE",
        package_count_band: packageCountBand(packageCountTotal()),
      });
    } finally {
      setLoading(false);
    }
  };

  const goSignIn = () => {
    if (!continueFired.current) {
      continueFired.current = true;
      pushEvent("continue_to_booking", {
        page_path: currentPagePath(), placement: "estimate_result", cta_name: "continue_to_booking",
        route_label: result && result.route_label,
        weight_band: weightBand(result && result.total_chargeable_weight_kg),
        package_count_band: packageCountBand(packageCountTotal()),
      });
    }
    // Stash Check Price inputs so the authenticated booking can prefill (Module 7.2E1).
    try {
      sessionStorage.setItem("air12_quote_prefill", JSON.stringify({
        pickup_address: form.pickup_address,
        pickup_pincode: form.pickup_pincode,
        delivery_address: form.delivery_address,
        delivery_pincode: form.delivery_pincode,
        lines,
        load_assist_side: loadSide,
      }));
    } catch { /* ignore storage errors */ }
    close(false);
    navigate("/login");
  };

  const submitEnquiry = async () => {
    setEnquiry((s) => ({ ...s, submitting: true }));
    try {
      await axios.post(`${API}/public/service-enquiry`, {
        requester_email: form.email.trim(),
        requester_phone: form.phone.trim() || null,
        pickup_address: form.pickup_address.trim() || null,
        pickup_pincode: form.pickup_pincode.trim() || null,
        delivery_address: form.delivery_address.trim() || null,
        delivery_pincode: form.delivery_pincode.trim() || null,
        packages: lines.map((l) => ({
          quantity: Number(l.quantity) || 1,
          actual_weight_kg_per_box: Number(l.actual_weight_kg_per_box) || 0.1,
          length_cm: Number(l.length_cm) || 1,
          width_cm: Number(l.width_cm) || 1,
          height_cm: Number(l.height_cm) || 1,
          package_type: l.package_type || "CARTON_BOX",
        })),
        reason_codes: (result?.reason_codes && result.reason_codes.length ? result.reason_codes : ["PRICE_UNAVAILABLE"]),
        customer_note: "Requested callback from public Check Price.",
        estimate_attempt_id: result?.attempt_id || null,
      });
      setEnquiry({ submitting: false, done: true });
      pushEvent("request_callback", {
        page_path: currentPagePath(), placement: "check_price_drawer", form_name: "service_enquiry", cta_name: "request_callback",
        reason_code: (result && result.reason_codes && result.reason_codes[0]) || "PRICE_UNAVAILABLE",
      });
    } catch {
      setEnquiry({ submitting: false, done: false });
    }
  };
  const isSuccess = result && result.status === "estimate_available";
  const unavailable = mapUnavailableReason(result ? result.reason_codes : []);

  return (
    <Sheet open={open} onOpenChange={close}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto rounded-none" data-testid="check-price-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 heading text-xl tracking-tight">
            <Calculator size={20} weight="bold" /> Check your Air12 price
          </SheetTitle>
          <SheetDescription className="sr-only">
            Get an indicative Air12 service price estimate by entering your pickup and delivery addresses and package details.
          </SheetDescription>
        </SheetHeader>

        {!result ? (
          <form onSubmit={runEstimate} className="mt-6 space-y-4" data-testid="check-price-form">
            {/* Contact — email required so Air12 captures the lead (Module 7.2E4) */}
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <div className="overline mb-1.5">Email <span className="text-[hsl(var(--destructive))]">*</span></div>
                <input type="email" className="input-field" value={form.email} onChange={setField("email")} placeholder="you@company.com" data-testid="lead-email" />
              </label>
              <label className="block">
                <div className="overline mb-1.5">Phone <span className="text-[hsl(var(--muted))]">(optional)</span></div>
                <input className="input-field" value={form.phone} onChange={setField("phone")} placeholder="+91…" data-testid="lead-phone" />
              </label>
            </div>
            <p className="text-[11px] text-[hsl(var(--muted))] -mt-2" data-testid="lead-email-note">Enter your email to view the estimate and receive Air12 follow-up if needed.</p>

            {/* Pickup */}
            <div className="grid grid-cols-3 gap-3">
              <label className="block col-span-2">
                <div className="overline mb-1.5">Pickup address</div>
                <input className="input-field" value={form.pickup_address} onChange={setField("pickup_address")} placeholder="Area, City, State" data-testid="pickup-address" />
              </label>
              <label className="block">
                <div className="overline mb-1.5">Pincode</div>
                <input className="input-field" value={form.pickup_pincode} onChange={setField("pickup_pincode")} placeholder="400069" data-testid="pickup-pincode" />
              </label>
            </div>
            {/* Delivery */}
            <div className="grid grid-cols-3 gap-3">
              <label className="block col-span-2">
                <div className="overline mb-1.5">Delivery address</div>
                <input className="input-field" value={form.delivery_address} onChange={setField("delivery_address")} placeholder="Area, City, State" data-testid="delivery-address" />
              </label>
              <label className="block">
                <div className="overline mb-1.5">Pincode</div>
                <input className="input-field" value={form.delivery_pincode} onChange={setField("delivery_pincode")} placeholder="110001" data-testid="delivery-pincode" />
              </label>
            </div>

            {/* Package lines */}
            <div className="pt-1">
              <div className="overline mb-2">Package details</div>
              <div className="space-y-3">
                {lines.map((l, i) => (
                  <div key={i} className="border border-[hsl(var(--border))] rounded-[2px] p-3" data-testid={`pkg-line-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-medium text-[hsl(var(--muted))]">Package type {i + 1}</div>
                      {lines.length > 1 && (
                        <button type="button" onClick={() => removeLine(i)} className="text-[hsl(var(--muted))] hover:text-[hsl(var(--destructive))]" data-testid={`pkg-${i}-remove`} aria-label="Remove package type">
                          <Trash size={15} weight="bold" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="block">
                        <div className="text-[11px] text-[hsl(var(--muted))] mb-1">Weight / box (kg)</div>
                        <input type="number" min="0" step="0.1" className="input-field" value={l.actual_weight_kg_per_box} onChange={setLine(i, "actual_weight_kg_per_box")} placeholder="20" data-testid={`pkg-${i}-weight`} />
                      </label>
                      <label className="block">
                        <div className="text-[11px] text-[hsl(var(--muted))] mb-1">Quantity</div>
                        <input type="number" min="1" className="input-field" value={l.quantity} onChange={setLine(i, "quantity")} placeholder="5" data-testid={`pkg-${i}-qty`} />
                      </label>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <label className="block">
                        <div className="text-[11px] text-[hsl(var(--muted))] mb-1">L (cm)</div>
                        <input type="number" min="0" className="input-field" value={l.length_cm} onChange={setLine(i, "length_cm")} placeholder="60" data-testid={`pkg-${i}-l`} />
                      </label>
                      <label className="block">
                        <div className="text-[11px] text-[hsl(var(--muted))] mb-1">W (cm)</div>
                        <input type="number" min="0" className="input-field" value={l.width_cm} onChange={setLine(i, "width_cm")} placeholder="40" data-testid={`pkg-${i}-w`} />
                      </label>
                      <label className="block">
                        <div className="text-[11px] text-[hsl(var(--muted))] mb-1">H (cm)</div>
                        <input type="number" min="0" className="input-field" value={l.height_cm} onChange={setLine(i, "height_cm")} placeholder="40" data-testid={`pkg-${i}-h`} />
                      </label>
                    </div>
                    <label className="block mt-2">
                      <div className="text-[11px] text-[hsl(var(--muted))] mb-1">Package type</div>
                      <select className="input-field" value={l.package_type} onChange={setLine(i, "package_type")} data-testid={`pkg-${i}-type`}>
                        {PACKAGE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </label>
                  </div>
                ))}
              </div>
              <button type="button" onClick={addLine} className="btn-ghost w-full justify-center mt-3 text-sm" data-testid="add-package-type-btn">
                <Plus size={14} weight="bold" /> Add another package type
              </button>
            </div>

            {/* Load Assist — Loading & Unloading Assistance (Module 7.2E4-O) */}
            <div className="pt-1" data-testid="load-assist-selector">
              <div className="overline mb-2">Need loading / unloading assistance?</div>
              <div className="grid grid-cols-2 gap-2">
                {LOAD_ASSIST_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setLoadSide(opt.value)}
                    className={`text-sm rounded-[2px] border px-3 py-2 text-left transition-colors ${
                      loadSide === opt.value
                        ? "border-[hsl(var(--accent))] bg-[hsl(var(--info-bg))] font-medium"
                        : "border-[hsl(var(--border))] hover:border-[hsl(var(--border-strong))]"
                    }`}
                    data-testid={`load-assist-option-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {error && <p className="text-sm text-[hsl(var(--destructive))]" data-testid="estimate-error">{error}</p>}

            <button type="submit" disabled={loading} className="btn-primary w-full justify-center mt-2 disabled:opacity-60" data-testid="calculate-estimate-btn">
              {loading ? (<><CircleNotch size={15} weight="bold" className="animate-spin" /> Calculating…</>) : (<>Calculate Estimate <ArrowRight size={14} weight="bold" /></>)}
            </button>
          </form>
        ) : isSuccess ? (
          <div className="mt-6" data-testid="check-price-result">
            <div className="overline">Estimated Air12 service payable</div>
            <div className="heading text-3xl tracking-tight mt-2" data-testid="estimate-total">₹{inr(result.air12_payable_estimate)}</div>
            <div className="text-xs text-[hsl(var(--muted))] mt-1" data-testid="estimate-range">
              Firm Air12 service price, confirmed at booking.
            </div>

            <dl className="mt-5 divide-y divide-[hsl(var(--border))] text-sm">
              <Row k="Route detected" v={result.route_label} testid="result-route" />
              <Row k="Chargeable weight" v={`${result.total_chargeable_weight_kg} kg · Zone ${result.pickup_zone} → ${result.delivery_zone}`} testid="result-weight-zone" />
              <Row k="Air12 service charge" v={`₹${inr(result.air12_service_subtotal)}`} testid="result-service-charge" />
              {result.load_assist && result.load_assist.available && result.load_assist.pickup && result.load_assist.pickup.selected && (
                <Row k={result.load_assist.pickup.label} v={`₹${inr(result.load_assist.pickup.amount)}`} testid="result-load-assist-pickup" />
              )}
              {result.load_assist && result.load_assist.available && result.load_assist.delivery && result.load_assist.delivery.selected && (
                <Row k={result.load_assist.delivery.label} v={`₹${inr(result.load_assist.delivery.amount)}`} testid="result-load-assist-delivery" />
              )}
              <Row k={`GST (${result.gst_rate}%)`} v={`₹${inr(result.gst_amount)}`} testid="result-gst" />
              <Row k="Total Air12 service payable" v={`₹${inr(result.air12_payable_estimate)}`} testid="result-total-payable" strong />
              <Row k="Cargo insurance" v="Added at booking · included in your Air12 total" testid="result-insurance" />
            </dl>

            <QuoteWeightRateFooter
              physical={result.total_actual_weight_kg}
              volumetric={result.total_volumetric_weight_kg}
              chargeable={result.total_chargeable_weight_kg}
              billable={result.billable_weight_kg}
              taxableSubtotal={result.taxable_subtotal}
            />

            <p className="text-[11px] text-[hsl(var(--muted))] mt-2" data-testid="estimate-payable-note">
              Indicative Air12 service only. At booking, Air12 adds mandatory cargo insurance (based on your declared value, before GST) into a single Air12 total — there is no separate insurance step or payment.
            </p>

            {result.load_assist && result.load_assist.available === false && (
              <div className="text-xs text-[hsl(var(--muted))] leading-relaxed mt-3 flex items-start gap-1.5" data-testid="load-assist-unavailable">
                <Info size={14} weight="bold" className="mt-0.5 shrink-0" />
                <span><strong className="text-[hsl(var(--ink))]">{LOAD_ASSIST_UNAVAILABLE_TITLE}.</strong> {LOAD_ASSIST_UNAVAILABLE_BODY}</span>
              </div>
            )}

            <p className="text-xs text-[hsl(var(--muted))] leading-relaxed mt-4">{result.disclaimer}</p>

            <button className="btn-primary w-full justify-center mt-5" onClick={goSignIn} data-testid="continue-to-signin-btn">
              Continue to Sign In / Booking <ArrowRight size={14} weight="bold" />
            </button>
            <button className="btn-ghost w-full justify-center mt-2" onClick={() => setResult(null)} data-testid="estimate-back-btn">
              Edit details
            </button>
          </div>
        ) : (
          <div className="mt-8 text-center" data-testid="check-price-unavailable">
            <div className="grid place-items-center w-12 h-12 mx-auto bg-[hsl(var(--info-bg))] text-[hsl(var(--accent))] rounded-[2px]">
              <Info size={24} weight="bold" />
            </div>
            <h3 className="heading text-xl tracking-tight mt-4" data-testid="price-unavailable-title">{unavailable.title}</h3>
            <p className="text-sm text-[hsl(var(--muted))] leading-relaxed mt-3" data-testid="price-unavailable-message">
              {unavailable.body}
            </p>

            {/* Service Enquiry — explicit "request a callback" lead (Module 7.2E4). Persisted only on
                click. No shipment / quote / package is ever created from this screen. */}
            <div className="mt-6 text-left" data-testid="service-enquiry-block">
              {enquiry.done ? (
                <p className="text-sm text-[hsl(var(--accent))] flex items-start gap-1.5" data-testid="service-enquiry-done">
                  <ShieldCheck size={15} weight="bold" className="mt-0.5 shrink-0" />
                  Thanks — your enquiry has been received. Air12 will review feasibility and pricing and get back to you.
                </p>
              ) : (
                <button className="btn-primary w-full justify-center" disabled={enquiry.submitting} onClick={submitEnquiry} data-testid="request-callback-btn">
                  {enquiry.submitting ? (<><CircleNotch size={14} weight="bold" className="animate-spin" /> Submitting…</>) : (unavailable.cta)}
                </button>
              )}
            </div>

            <button className="btn-ghost w-full justify-center mt-3" onClick={() => setResult(null)} data-testid="estimate-back-btn">
              {unavailable.secondary}
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Row({ k, v, testid, strong }) {
  return (
    <div className="flex items-center justify-between py-2" data-testid={testid}>
      <dt className={strong ? "font-semibold text-[hsl(var(--fg))]" : "text-[hsl(var(--muted))]"}>{k}</dt>
      <dd className={`text-right ${strong ? "font-semibold" : "font-medium"}`}>{v}</dd>
    </div>
  );
}
